import { type RefObject, useLayoutEffect, useRef } from "react";
import type { Task } from "@/api/tasks";
import { useTasksStore } from "@/stores/tasks-store";
import { calendarOpening, localDateKey, taskDueDate } from "./task-dates";

interface Opening {
  target: ReturnType<typeof calendarOpening> | null;
  /** The month the opening replaced, once it asked for its own. */
  replacedMonth: Date | null;
  done: boolean;
}

function sameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

/**
 * Opens the calendar on upcoming work (`calendarOpening`), once per mount
 * and once the tasks loaded: on the month that shows the next due card,
 * scrolled so its opening row is at the top of `scrollRef`.
 */
export function useCalendarOpening(
  scrollRef: RefObject<HTMLElement | null>,
  tasks: readonly Task[],
  today: Date,
): void {
  const loaded = useTasksStore((state) => state.loaded);
  const month = useTasksStore((state) => state.calendarMonth);
  const setMonth = useTasksStore((state) => state.setCalendarMonth);
  const opening = useRef<Opening>({
    target: null,
    replacedMonth: null,
    done: false,
  });
  useLayoutEffect(() => {
    const state = opening.current;
    if (!loaded || state.done) return;
    state.target ??= calendarOpening(
      tasks.flatMap((task) => taskDueDate(task.due_at) ?? []),
      today,
    );
    if (!sameMonth(state.target.month, month)) {
      if (!state.replacedMonth) {
        state.replacedMonth = month;
        setMonth(state.target.month);
        return;
      }
      // Still the month it replaced (a re-run before it rendered), or
      // another month picked meanwhile, which is the user's.
      if (month !== state.replacedMonth) state.done = true;
      return;
    }
    state.done = true;
    const scroller = scrollRef.current;
    const row = scroller?.querySelector<HTMLElement>(
      `[data-calendar-day="${localDateKey(state.target.topDay)}"]`,
    );
    if (scroller && row) scroller.scrollTop = row.offsetTop;
  }, [loaded, month, setMonth, tasks, today, scrollRef]);
}
